import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/date-range-picker';
import { useSalesFilters } from '@/lib/sales-filters';
import { getBills, exportUrl } from '@/lib/sales-api';
import { fmtINR, fmtDate } from '@/lib/format';
import { useAuth } from '@/auth/use-auth';

export const Route = createFileRoute('/_app/sales/bills')({ component: Page });

const PAGE_SIZE = 50;

function Page() {
  const { from, to, store, page, setFilters } = useSalesFilters();
  const { user } = useAuth();
  const nav = useNavigate();

  const q = useQuery({
    queryKey: ['sales:bills', from, to, store, page],
    queryFn: () => getBills({ from, to, store, page, limit: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  const canExport = user && ['admin','owner','manager','accountant'].includes(user.role);
  const totalPages = q.data ? Math.max(1, Math.ceil(q.data.total / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <DateRangePicker from={from} to={to} onChange={(r) => setFilters({ from: r.from, to: r.to, page: 1 })} />
        {canExport && (
          <Button asChild variant="outline">
            <a href={exportUrl({ from, to, store })} download>Export CSV</a>
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Bills {q.data && <span className="text-sm font-normal text-muted-foreground">({q.data.total} total)</span>}</CardTitle>
        </CardHeader>
        <CardContent>
          {q.isLoading && <p>Loading…</p>}
          {q.error && <p className="text-sm text-destructive">{(q.error as Error).message}</p>}
          {q.data && (
            <>
              <Table>
                <THead>
                  <TR>
                    <TH>Bill #</TH><TH>Date</TH><TH>Store</TH>
                    <TH className="text-right">Net</TH>
                    <TH className="text-right">Total</TH>
                    <TH>Tender</TH>
                  </TR>
                </THead>
                <TBody>
                  {q.data.bills.map((b) => {
                    const onOpen = () => nav({
                      to: '/sales/bill/$storeCode/$billDate/$billNo',
                      params: { storeCode: b.storeCode, billDate: b.billDate, billNo: b.billNo },
                    });
                    return (
                      <TR
                        key={`${b.billDate}-${b.storeCode}-${b.billNo}`}
                        className="cursor-pointer"
                        tabIndex={0}
                        role="button"
                        aria-label={`Open bill ${b.billNo}`}
                        onClick={onOpen}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
                      >
                        <TD className="font-mono">{b.billNo}</TD>
                        <TD>{fmtDate(b.billDate)}</TD>
                        <TD>{b.storeCode}</TD>
                        <TD className="text-right">{fmtINR(b.netAmt)}</TD>
                        <TD className="text-right">{fmtINR(b.billAmt)}</TD>
                        <TD>{b.tender}</TD>
                      </TR>
                    );
                  })}
                </TBody>
              </Table>

              <div className="mt-3 flex items-center justify-end gap-2 text-sm">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setFilters({ page: page - 1 })}>Prev</Button>
                <span className="px-2">Page {page} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setFilters({ page: page + 1 })}>Next</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
